import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { cn } from "~/lib/cn";
import { updateUser } from "~/lib/functions/user";
import { updateClerkEmailAddresses, updateClerkUser } from "~/lib/clerk";
import { isClerkAPIResponseError } from "~/lib/clerk";
import { authMiddleware } from "~/lib/auth-middleware";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { ImageInput } from "~/components/file-input";
import { OtpInput } from "~/components/ui/otp-input";
import { FormField } from "~/components/ui/form-field";
import { UserProfileSchema } from "~/lib/schemas/user-auth";
import type { UserProfileForm } from "~/lib/schemas/user-auth";
import { useClerk } from "@clerk/tanstack-react-start";
import { createNoIndexMeta } from "~/lib/shared/meta";

// Server function to get user profile data
export const getUserProfile = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(({ context }) => {
    const user = context.user;
    return {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || undefined,
      photo: user?.profilePictureUrl || undefined,
      clerkUserId: user?.clerkUserId || undefined,
    };
  });

// Server function to update user profile
export const updateUserProfile = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(UserProfileSchema)
  .handler(async ({ data, context }) => {
    try {
      const userId = context.user?.id;
      if (!userId) {
        throw new Error("User not authenticated");
      }

      await updateUser({ data: { userId, user: data } });

      if (context.user.clerkUserId) {
        await updateClerkUser({
          data: {
            userId: context.user.clerkUserId,
            params: {
              firstName: data.firstName,
              lastName: data.lastName,
            },
          },
        });
      }

      return {
        status: "success",
        message: "User updated",
      };
    } catch {
      // Re-throw with user-friendly message
      throw new Error("Failed to update user");
    }
  });

export const userProfileQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: ["userProfile", userId],
    queryFn: () => getUserProfile(),
    staleTime: Infinity,
  });

export const Route = createFileRoute("/_user/settings/profile/")({
  head: () =>
    createNoIndexMeta(
      "Profile Settings – Dream Coach Match",
      "Update your profile information",
    ),
  loader: async ({ context }) => {
    const userId = context.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    await context.queryClient.ensureQueryData(userProfileQueryOptions(userId));
    return {};
  },
  component: ProfileSettingsPage,
});

function ProfileSettingsPage() {
  const queryClient = useQueryClient();
  const clerk = useClerk();
  const userId = clerk.user?.id;
  if (!userId) {
    throw new Error("User not authenticated");
  }

  const { data: profileData } = useQuery(userProfileQueryOptions(userId));

  const [isProcessing, setIsProcessing] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [emailId, setEmailId] = useState<string>();

  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });

  const { register, handleSubmit, formState, watch, getValues, setError } =
    useForm<UserProfileForm>({
      resolver: zodResolver(UserProfileSchema),
      defaultValues: profileData || {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: undefined,
        photo: undefined,
        clerkUserId: undefined,
      },
    });

  const { errors } = formState;

  const updateProfileMutation = useMutation({
    mutationFn: (data: UserProfileForm) => updateUserProfile({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userProfile", userId] });
    },
    onError: (error: Error) => {
      setError("root", {
        message: error.message || "Failed to update profile",
      });
    },
  });

  // OTP handlers
  const startResendTimer = () => {
    setState((prev) => ({
      ...prev,
      resendTimer: 60,
      canResend: false,
    }));

    const timer = setInterval(() => {
      setState((prev) => {
        const newTimer = prev.resendTimer - 1;
        if (0 >= newTimer) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: newTimer };
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    try {
      await handleSubmit(() => {
        /* No operation needed */
      })();
    } catch {
      return;
    }

    startResendTimer();
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        return;
      }
      if (!clerk.user?.id) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return;
      }

      const email = getValues("email");
      const res = await clerk.user.createEmailAddress({ email });
      const emailAddress = clerk.user.emailAddresses.find(
        (a) => a.id === res.id,
      );
      if (emailAddress) {
        setEmailId(emailAddress.id);
        // Send the user an email with the verification code
        await emailAddress.prepareVerification({ strategy: "email_code" });
      }
      setShowOtpField(true);
      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);

      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error:
            err.errors[0]?.longMessage || "Failed to create sign up attempt",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
    }
  };

  const handleValidateCode = () => {
    try {
      z.object({ code: z.array(z.string()) }).parse({ code: state.code });
      setState((prev) => ({ ...prev, error: "" }));
      return true;
    } catch (error: unknown) {
      const errorMessage =
        error && "object" === typeof error && "issues" in error
          ? Array.isArray((error as { issues: unknown[] }).issues)
            ? (error as { issues: { message?: string }[] }).issues[0]
                ?.message || "Invalid input"
            : "Invalid input"
          : "Invalid input";
      setState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
      return false;
    }
  };

  const handleVerifyOtp = async () => {
    if (!(await handleValidateCode())) {
      return;
    }

    try {
      if (!clerk?.client) {
        return;
      }
      if (!clerk?.user?.id) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return;
      }

      if (!emailId) {
        setState((prev) => ({
          ...prev,
          error: "No email verification in progress",
        }));
        return;
      }

      const emailToVerify = clerk.user.emailAddresses.find(
        (email) => email.id === emailId,
      );

      if (!emailToVerify) {
        setState((prev) => ({ ...prev, error: "Email address not found" }));
        return;
      }

      await emailToVerify.attemptVerification({
        code: state.code.join(""),
      });

      // After successful verification, update the email addresses
      await updateClerkEmailAddresses({ data: { emailAddressId: emailId } });
      handleSubmit(onSubmit)();
      setShowOtpField(false);
      setState((prev) => ({ ...prev, code: ["", "", "", "", "", ""] }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: `Failed to verify OTP code: ${error}`,
      }));
    }
  };

  const handleOtpChange = (code: string[]) => {
    setState((prev) => ({ ...prev, code: [...code] }));
  };

  const handleOtpComplete = (code: string) => {
    setState((prev) => ({ ...prev, code: code.split("") }));
    handleVerifyOtp();
  };

  const onSubmit = (data: UserProfileForm) => {
    updateProfileMutation.mutate(data);
  };

  const handleSaveUserProfile = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <div className="mx-auto mb-4 flex w-full max-w-screen-xl flex-col overflow-visible">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="absolute right-6 top-0">
            <Button
              size="sm"
              loading={updateProfileMutation.isPending}
              disabled={updateProfileMutation.isPending}
              onClick={handleSaveUserProfile}
            >
              Save changes
            </Button>
          </div>
          <Card className="mb-8 flex space-x-8 p-8 hover:shadow">
            <FormField error={errors.photo?.message}>
              <ImageInput
                {...register("photo")}
                value={watch("photo")}
                type="url"
                error={errors.photo?.message}
                accept=".jpg,.jpeg,.png"
              />
            </FormField>

            <div className="w-full">
              <h2 className="text-xl font-semibold">Personal Details</h2>
              <div className="grid gap-6 md:grid-cols-2">
                <FormField label="First name" error={errors.firstName?.message}>
                  <Input
                    {...register("firstName")}
                    error={errors.firstName?.message}
                    placeholder="Enter your first name"
                  />
                </FormField>
                <FormField label="Last name" error={errors.lastName?.message}>
                  <Input
                    {...register("lastName")}
                    error={errors.lastName?.message}
                    placeholder="Enter your last name"
                  />
                </FormField>
              </div>
            </div>
          </Card>

          <Card className="flex flex-col p-8 hover:shadow">
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Email and Phone Details</h2>
            </div>

            <div className="flex gap-x-8">
              <div className="w-1/2">
                <div
                  className={cn("flex max-h-fit gap-4 opacity-100", {
                    "max-h-0 opacity-0": showOtpField,
                  })}
                >
                  <FormField
                    fullWidth
                    label="Email Address"
                    error={errors.email?.message || state.error}
                  >
                    <Input
                      {...register("email")}
                      type="email"
                      className="w-full"
                    />
                  </FormField>
                  <Button
                    type="button"
                    loading={isProcessing}
                    onClick={handleSendOtp}
                    className={cn("mb-2", {
                      "place-self-end": !errors.email || !state.error,
                      "place-self-center": errors.email || state.error,
                    })}
                  >
                    Update
                  </Button>
                </div>
                <div id="clerk-captcha" className="w-full" />
                <div
                  className={cn("max-h-0 opacity-0", {
                    "max-h-fit opacity-100": showOtpField,
                  })}
                >
                  <div className="flex items-end gap-4 transition-all duration-300 ease-in-out">
                    <OtpInput
                      value={state.code}
                      onChange={handleOtpChange}
                      onComplete={handleOtpComplete}
                      error={state.error}
                      label="Check Your Email"
                      description={`We sent a code to ${watch("email")}. Enter 6 digit code that mentioned in the email`}
                    />
                  </div>
                  <Button
                    type="button"
                    className="mb-2 w-full"
                    onClick={handleSendOtp}
                    disabled={!state.canResend}
                  >
                    {state.canResend
                      ? "Resend code"
                      : `Resend code (${state.resendTimer}s)`}
                  </Button>
                </div>
                <div className="mt-2 flex items-center bg-gray-200 px-5 py-3 text-sm">
                  Note: You'll get confirmation email within 30 min
                </div>
              </div>

              <div className="w-1/2">
                <div className="flex items-end gap-4">
                  <FormField
                    label="Phone number"
                    fullWidth
                    error={errors.phoneNumber?.message}
                  >
                    <Input
                      {...register("phoneNumber")}
                      error={errors.phoneNumber?.message}
                      type="tel"
                      className="w-full"
                    />
                  </FormField>
                  <Input {...register("clerkUserId")} type="hidden" />
                  <Button
                    type="button"
                    className="mb-2"
                    onClick={handleVerifyOtp}
                  >
                    Verify
                  </Button>
                </div>
                <div className="mt-2 flex items-center bg-gray-200 px-5 py-3 text-sm">
                  Note: You'll get confirmation OTP within 30 min
                </div>
              </div>
            </div>
          </Card>
        </form>
      </div>
    </>
  );
}
